import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Row, Col, Card, DatePicker, Select, InputNumber, Space, Table, Radio, Button, Alert, Spin, Tag, Switch } from 'antd';
import { CalendarOutlined, ReloadOutlined, DollarOutlined, BankOutlined, LineChartOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../libs/api';
import { computeShForecast, aggregateByPeriod } from '../utils/financialPlanning';

const HORIZON_OPTIONS = [
  { value: 3, label: '3 months' },
  { value: 6, label: '6 months' },
  { value: 12, label: '12 months' },
];

function fmt(v) {
  if (v == null || isNaN(v)) return '-';
  const n = Number(v);
  const abs = Math.abs(n);
  const formatted = '$' + Math.round(abs).toLocaleString();
  if (n < 0) return <span style={{ color: '#cf1322' }}>-{formatted}</span>;
  return formatted;
}

const moneyFmt = (v) => {
  if (v == null || v === '') return '';
  const num = parseFloat(v);
  if (isNaN(num)) return String(v);
  const rounded = Math.round(num * 100) / 100;
  const parts = rounded.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return '$ ' + parts.join('.');
};
const moneyParse = (v) => v.replace(/\$\s?|,/g, '');

export default function ShinehubPlanner() {
  const [startMonth, setStartMonth] = useState(dayjs());
  const [horizonMonths, setHorizonMonths] = useState(6);
  const [granularity, setGranularity] = useState('monthly');

  // Opening balances (fetched from Xero, editable). ShineHub doesn't carry inventory.
  const [opening, setOpening] = useState({
    cash: 0, accountsReceivable: 0, inventory: 0,
    otherAssets: 0, accountsPayable: 0, otherLiabilities: 0, gstPayable: 0,
  });
  const [openingAsOf, setOpeningAsOf] = useState(null);
  const [openingLoading, setOpeningLoading] = useState(false);
  const [openingError, setOpeningError] = useState(null);

  const fetchOpening = useCallback(async () => {
    setOpeningLoading(true);
    setOpeningError(null);
    try {
      const res = await api.get('/api/financial-planning/opening', {
        params: { entity: 'sh', month: startMonth.format('YYYY-MM') },
      });
      const d = res.data.data;
      setOpening({
        cash: d.cash || 0,
        accountsReceivable: d.accountsReceivable || 0,
        inventory: 0, // not modelled for ShineHub
        otherAssets: (d.otherAssets || 0) + (d.inventory || 0) + (d.depositsToSuppliers || 0), // roll inventory + deposits into Other for SH
        accountsPayable: d.accountsPayable || 0,
        otherLiabilities: d.otherLiabilities || 0,
        gstPayable: d.gstPayable || 0,
      });
      setOpeningAsOf(d.asOfDate || null);
    } catch (err) {
      setOpeningError(err.response?.data?.message || 'Failed to load opening balance from Xero');
    }
    setOpeningLoading(false);
  }, [startMonth]);

  // Baseline (sales mix, per-system metrics, lags)
  const [baseline, setBaseline] = useState(null);
  const [baselineLoading, setBaselineLoading] = useState(false);
  const [baselineError, setBaselineError] = useState(null);

  // Pipeline (existing projects)
  const [pipeline, setPipeline] = useState([]);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [monthsBack, setMonthsBack] = useState(6);
  // Debug toggle: include existing pipeline projects in the forecast (or only new sales).
  const [includeExisting, setIncludeExisting] = useState(true);

  // Editable assumptions — per payment type
  const [byType, setByType] = useState({
    Upfront: { avgRevenue: 0, avgCrmPayment: 0, cogsPct: 0.75 },
    PPA:     { avgRevenue: 0, avgCrmPayment: 0, cogsPct: 0.75 },
    BNPL:    { avgRevenue: 0, avgCrmPayment: 0, cogsPct: 0.75 },
  });
  const [monthlyOpex, setMonthlyOpex] = useState(0);
  const [opexDetail, setOpexDetail] = useState([]);
  const [avgClosingToInstallDays, setAvgClosingToInstallDays] = useState(90);
  const updateType = (type, key, val) => {
    setByType(prev => ({ ...prev, [type]: { ...prev[type], [key]: val } }));
  };

  // Drivers — single totalSales per month + total installs. Per-type breakdown auto from mix.
  const [drivers, setDrivers] = useState([
    { totalSales: 0, totalInstalls: 0 },
    { totalSales: 0, totalInstalls: 0 },
    { totalSales: 0, totalInstalls: 0 },
  ]);
  const updateDriver = (idx, key, val) => {
    setDrivers(prev => prev.map((d, i) => i === idx ? { ...d, [key]: val || 0 } : d));
  };

  // Past 3-month mix ratios (used to break down totalSales into per-type counts)
  const mix = useMemo(() => {
    const total = (baseline?.byType?.Upfront?.count || 0)
      + (baseline?.byType?.PPA?.count || 0)
      + (baseline?.byType?.BNPL?.count || 0);
    if (total === 0) return { Upfront: 1, PPA: 0, BNPL: 0 };
    return {
      Upfront: (baseline?.byType?.Upfront?.count || 0) / total,
      PPA: (baseline?.byType?.PPA?.count || 0) / total,
      BNPL: (baseline?.byType?.BNPL?.count || 0) / total,
    };
  }, [baseline]);

  // Compute per-type counts for each month from totalSales × mix
  const driversForEngine = useMemo(() => drivers.map(d => {
    const total = d.totalSales || 0;
    const upfront = Math.round(total * mix.Upfront);
    const ppa = Math.round(total * mix.PPA);
    const bnpl = Math.max(0, total - upfront - ppa);
    return { totalSales: total, salesUpfront: upfront, salesPPA: ppa, salesBNPL: bnpl, totalInstalls: d.totalInstalls || 0 };
  }), [drivers, mix]);

  // pristine guards "first prefill". Use a ref so it doesn't appear in deps and cause re-fetch loops.
  const pristineRef = useRef(true);

  // Fetch SH baseline (per-type counts, avg rev/payment), retail-finance summary (per-project GP), Xero opex baseline
  const fetchBaseline = useCallback(async () => {
    setBaselineLoading(true);
    setBaselineError(null);
    try {
      // Past 3 FULL calendar months before the planning start month (matches backend)
      const planMonthStart = startMonth.startOf('month');
      const sumStart = planMonthStart.subtract(3, 'month').format('YYYY-MM-DD');
      const sumEnd = planMonthStart.subtract(1, 'day').format('YYYY-MM-DD');

      const [baseRes, summaryRes, opexRes] = await Promise.all([
        api.get('/api/financial-planning/sh/baseline', { params: { month: startMonth.format('YYYY-MM') } }),
        api.get('/api/retail-finance/summary', { params: { dateField: 'closingDate', start: sumStart, end: sumEnd } }),
        api.get('/api/financial-planning/opex-baseline', { params: { entity: 'sh', month: startMonth.format('YYYY-MM') } }).catch(() => null),
      ]);

      const d = baseRes.data.data;

      // Aggregate per-type metrics from retail-finance summary (handles PPA revenue correctly via OC quote)
      // Use CRM designed GP (avg of gpMin/gpMax from OC quote) instead of Xero actual gp,
      // because recently-closed projects don't have full Xero invoice data yet.
      const sumProjects = summaryRes.data?.data?.projects || [];
      const byTypeAgg = {
        Upfront: { count: 0, sumRev: 0, sumPayment: 0, sumCrmGp: 0, gpCount: 0 },
        PPA:     { count: 0, sumRev: 0, sumPayment: 0, sumCrmGp: 0, gpCount: 0 },
        BNPL:    { count: 0, sumRev: 0, sumPayment: 0, sumCrmGp: 0, gpCount: 0 },
      };
      let totalCount = 0;
      for (const p of sumProjects) {
        const pm = (p.paymentMethod || '').toLowerCase();
        const type = /ppa/.test(pm) ? 'PPA' : (/bnpl/.test(pm) ? 'BNPL' : 'Upfront');
        const t = byTypeAgg[type];
        t.count++;
        t.sumRev += p.revenue || 0;
        t.sumPayment += p.payment || 0;
        if (p.gpMin != null && p.gpMax != null && (p.revenue || 0) > 0) {
          t.sumCrmGp += (p.gpMin + p.gpMax) / 2;
          t.gpCount++;
        }
        totalCount++;
      }

      // Override d.byType with accurate aggregates from summary
      const richBaseline = {
        ...d,
        systemCount: totalCount || d.systemCount,
        avgPerMonthCount: Math.round((totalCount || d.systemCount) / 3),
        byType: {
          Upfront: { count: byTypeAgg.Upfront.count, avgRevenue: byTypeAgg.Upfront.count ? Math.round(byTypeAgg.Upfront.sumRev / byTypeAgg.Upfront.count) : 0, avgCrmPayment: byTypeAgg.Upfront.count ? Math.round(byTypeAgg.Upfront.sumPayment / byTypeAgg.Upfront.count) : 0 },
          PPA:     { count: byTypeAgg.PPA.count,     avgRevenue: byTypeAgg.PPA.count     ? Math.round(byTypeAgg.PPA.sumRev / byTypeAgg.PPA.count)         : 0, avgCrmPayment: byTypeAgg.PPA.count     ? Math.round(byTypeAgg.PPA.sumPayment / byTypeAgg.PPA.count)         : 0 },
          BNPL:    { count: byTypeAgg.BNPL.count,    avgRevenue: byTypeAgg.BNPL.count    ? Math.round(byTypeAgg.BNPL.sumRev / byTypeAgg.BNPL.count)        : 0, avgCrmPayment: byTypeAgg.BNPL.count    ? Math.round(byTypeAgg.BNPL.sumPayment / byTypeAgg.BNPL.count)        : 0 },
        },
      };
      setBaseline(richBaseline);

      // GP% per type from designed CRM GP (gpMin/gpMax avg from OC quote), divided by CRM revenue
      // (only counts projects where both quote data and CRM revenue are present)
      const gpPctOf = (a) => (a.gpCount > 0 && a.sumRev > 0 ? a.sumCrmGp / a.sumRev : null);

      if (pristineRef.current && totalCount > 0) {
        setByType(prev => {
          const next = { ...prev };
          for (const type of ['Upfront', 'PPA', 'BNPL']) {
            const t = richBaseline.byType[type];
            const gpPct = gpPctOf(byTypeAgg[type]);
            next[type] = {
              avgRevenue: t.avgRevenue || 0,
              avgCrmPayment: t.avgCrmPayment || 0,
              cogsPct: gpPct != null ? Math.max(0, Math.min(1, 1 - gpPct)) : 0.75,
            };
          }
          return next;
        });
        setAvgClosingToInstallDays(d.avgClosingToInstallDays || 90);
        setDrivers(prev => prev.map((row, i) => {
          const actuals = (d.driverMonthActuals || [])[i] || { closings: 0, installs: 0 };
          const baseAvg = richBaseline.avgPerMonthCount || 0;
          return {
            totalSales: Math.max(actuals.closings, baseAvg),
            totalInstalls: Math.max(actuals.installs, row.totalInstalls || baseAvg),
          };
        }));
        pristineRef.current = false;
      }

      if (opexRes && opexRes.data?.data?.monthlyOpex != null) {
        setMonthlyOpex(opexRes.data.data.monthlyOpex || 0);
        setOpexDetail(opexRes.data.data.detail || []);
      }
    } catch (err) {
      setBaselineError(err.response?.data?.message || 'Failed to load ShineHub baseline');
    }
    setBaselineLoading(false);
  }, [startMonth]);

  // Fetch pipeline
  const fetchPipeline = useCallback(async () => {
    setPipelineLoading(true);
    try {
      const res = await api.get('/api/financial-planning/sh/pipeline', { params: { monthsBack } });
      setPipeline(res.data.data?.projects || []);
    } catch {
      setPipeline([]);
    }
    setPipelineLoading(false);
  }, [monthsBack]);

  useEffect(() => { fetchBaseline(); fetchPipeline(); fetchOpening(); }, [fetchBaseline, fetchPipeline, fetchOpening]);

  // Compute forecast
  const forecast = useMemo(() => {
    return computeShForecast({
      existingPipeline: includeExisting ? pipeline : [],
      monthlyDrivers: driversForEngine,
      assumptions: {
        byType,
        avgClosingToInstallDays,
        monthlyOpex,
      },
      openingBalances: opening,
      horizonMonths,
      startDate: startMonth.startOf('month').format('YYYY-MM-DD'),
      today: dayjs().format('YYYY-MM-DD'),
      monthActuals: baseline?.driverMonthActuals || [],
      includeExistingActuals: includeExisting,
    });
  }, [pipeline, includeExisting, driversForEngine, byType, avgClosingToInstallDays, monthlyOpex, opening, horizonMonths, startMonth, baseline]);

  // Per-driver-month actuals from baseline (for input minimums)
  const monthActuals = baseline?.driverMonthActuals || [];

  // Per-driver-month metrics from the resolved forecast:
  //   closingToInstall = projects closed but not yet installed by the last
  //                       calendar day of the month (= "pipeline at EOM").
  //   installsCompleted = installs that the forecast engine actually
  //                       placed in this month. May be < driver target if
  //                       the closing→install lag (see avgClosingToInstallDays,
  //                       user-editable, defaults to baseline value) caps
  //                       eligibility.
  const pipelineByMonth = useMemo(() => drivers.map((_, i) => {
    const monthStart = startMonth.startOf('month').add(i, 'month').format('YYYY-MM-DD');
    const monthEnd = startMonth.startOf('month').add(i + 1, 'month').subtract(1, 'day').format('YYYY-MM-DD');
    let closingToInstall = 0;
    let installsCompleted = 0;
    for (const p of forecast.pipeline) {
      if (p.installDate && p.installDate >= monthStart && p.installDate <= monthEnd) {
        installsCompleted++;
      }
      if (!p.closingDate || p.closingDate > monthEnd) continue;
      if (!p.installDate || p.installDate > monthEnd) closingToInstall++;
    }
    return { closingToInstall, installsCompleted };
  }), [drivers, startMonth, forecast]);

  const rows = useMemo(() =>
    aggregateByPeriod(forecast.days, granularity === 'weekly' ? 'week' : 'month'),
    [forecast, granularity]
  );

  const buildTable = (lineItems, dataKey) => {
    const periodCols = rows.map((r, i) => ({
      title: r.label, dataIndex: `col${i}`, align: 'right', width: 110,
    }));
    const tableData = lineItems.map(({ key, label, bold }) => {
      const row = { key, _label: label, _bold: bold };
      rows.forEach((r, i) => { row[`col${i}`] = r[dataKey][key]; });
      return row;
    });
    const columns = [
      { title: '', dataIndex: '_label', width: 220, fixed: 'left', render: (v, rec) => rec._bold ? <b>{v}</b> : v },
      ...periodCols,
    ];
    return { columns, tableData };
  };

  const renderCell = (v, rec) => rec._bold ? <b>{fmt(v)}</b> : fmt(v);
  const applyRender = (cols) => cols.map(c => c.dataIndex === '_label' ? c : { ...c, render: renderCell });

  const plItems = [
    { key: 'revenue', label: 'Revenue' },
    { key: 'cogs', label: 'Cost of Goods Sold' },
    { key: 'gp', label: 'Gross Profit', bold: true },
    { key: 'opex', label: 'Operating Expenses' },
    { key: 'np', label: 'Net Profit', bold: true },
  ];
  const bsItems = [
    { key: 'cash', label: 'Cash' },
    { key: 'ar', label: 'Accounts Receivable (incl GST)' },
    { key: 'otherAssets', label: 'Xero Other Assets' },
    { key: 'totalAssets', label: 'Total Assets', bold: true },
    { key: 'ap', label: 'Accounts Payable (incl GST)' },
    { key: 'gstPayable', label: 'Net GST Payable' },
    { key: 'otherLiabilities', label: 'Xero Other Liabilities' },
    { key: 'totalLiabilities', label: 'Total Liabilities', bold: true },
    { key: 'netAssets', label: 'Net Assets', bold: true },
    { key: 'retainedEarnings', label: 'Cumulative Net Profit' },
  ];
  const cfItems = [
    { key: 'cashIn', label: 'Cash In (project receipts)' },
    { key: 'cashOutPurchases', label: 'Cash Out (COGS payments)' },
    { key: 'cashOutOpex', label: 'Cash Out (opex)' },
    { key: 'netCf', label: 'Net Cashflow', bold: true },
    { key: 'closingCash', label: 'Closing Cash Balance', bold: true },
  ];
  const pl = buildTable(plItems, 'pl');
  const bs = buildTable(bsItems, 'bs');
  const cf = buildTable(cfItems, 'cf');

  // Pipeline display: only show projects in the active forecast window (in pipeline, or installDate within horizon)
  const horizonStart = startMonth.startOf('month');
  const horizonEnd = horizonStart.add(horizonMonths, 'month');
  const pipelineForDisplay = useMemo(() => {
    return forecast.pipeline.filter(p => {
      const dates = [p.closingDate, p.installDate].filter(Boolean);
      if (dates.length === 0) return false;
      // Show if not yet installed (still in pipeline) OR install date falls within forecast window
      if (!p.installDate) return true;
      const inst = dayjs(p.installDate);
      return inst.isAfter(horizonStart) && inst.isBefore(horizonEnd);
    }).slice(0, 500);
  }, [forecast, horizonStart, horizonEnd]);

  return (
    <>
      {baselineError && <Alert message={baselineError} type="warning" showIcon closable style={{ marginBottom: 16 }} onClose={() => setBaselineError(null)} />}

      {/* Controls */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap size="middle" align="center">
          <CalendarOutlined style={{ color: '#54A2CC' }} />
          <span style={{ fontSize: 12, color: '#666' }}>Start month:</span>
          <DatePicker picker="month" value={startMonth} onChange={d => d && setStartMonth(d)} format="MMM YYYY" size="small" allowClear={false} />
          <span style={{ fontSize: 12, color: '#666' }}>Horizon:</span>
          <Select value={horizonMonths} onChange={setHorizonMonths} options={HORIZON_OPTIONS} size="small" style={{ width: 110 }} />
          <span style={{ fontSize: 12, color: '#666' }}>View:</span>
          <Radio.Group value={granularity} onChange={e => setGranularity(e.target.value)} size="small" optionType="button" buttonStyle="solid">
            <Radio.Button value="monthly">Monthly</Radio.Button>
            <Radio.Button value="weekly">Weekly</Radio.Button>
          </Radio.Group>
          <Button size="small" icon={<ReloadOutlined />} onClick={() => { fetchBaseline(); fetchPipeline(); fetchOpening(); }}>Refresh</Button>
          <span style={{ borderLeft: '1px solid #ddd', height: 18, marginLeft: 4 }} />
          <span style={{ fontSize: 12, color: '#666' }}>Existing pipeline:</span>
          <Switch
            size="small"
            checked={includeExisting}
            onChange={setIncludeExisting}
            checkedChildren="On"
            unCheckedChildren="Off"
          />
          <span style={{ fontSize: 11, color: '#999' }}>
            {includeExisting ? 'forecast = existing + new sales' : 'forecast = new sales only'}
          </span>
        </Space>
      </Card>

      {openingError && <Alert message={openingError} type="warning" showIcon closable style={{ marginBottom: 16 }} onClose={() => setOpeningError(null)} />}

      {/* Opening Balances */}
      <Card
        title={
          <>
            <BankOutlined /> Opening Balances (from Xero)
            {openingAsOf && <Tag color="blue" style={{ marginLeft: 8, fontWeight: 'normal' }}>as of {dayjs(openingAsOf).format('D MMM YYYY')}</Tag>}
          </>
        }
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Spin spinning={openingLoading}>
          {(() => {
            const Field = ({ label, k }) => (
              <Col span={6}>
                <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>{label}</div>
                <InputNumber value={opening[k]} onChange={v => setOpening(p => ({ ...p, [k]: v || 0 }))} style={{ width: '100%' }} formatter={moneyFmt} parser={moneyParse} />
              </Col>
            );
            return (
              <>
                <div style={{ fontSize: 12, color: '#54A2CC', fontWeight: 600, margin: '4px 0 8px' }}>Assets</div>
                <Row gutter={[16, 12]}>
                  <Field label="Cash" k="cash" />
                  <Field label="Accounts Receivable" k="accountsReceivable" />
                  <Field label="Xero Other Assets" k="otherAssets" />
                </Row>
                <div style={{ fontSize: 12, color: '#54A2CC', fontWeight: 600, margin: '12px 0 8px' }}>Liabilities</div>
                <Row gutter={[16, 12]}>
                  <Field label="Accounts Payable" k="accountsPayable" />
                  <Field label="Net GST Payable" k="gstPayable" />
                  <Field label="Xero Other Liabilities" k="otherLiabilities" />
                </Row>
                <div style={{ fontSize: 11, color: '#999', marginTop: 8 }}>
                  ShineHub does not carry inventory; any inventory or supplier-deposit balances from Xero are rolled into Other Assets.
                </div>
              </>
            );
          })()}
        </Spin>
      </Card>

      {/* Assumptions */}
      <Card title="Assumptions — per payment type (pre-filled from past 3 months closed)" size="small" style={{ marginBottom: 16 }}>
        <Spin spinning={baselineLoading}>
          <Table
            size="small"
            pagination={false}
            dataSource={['Upfront', 'PPA', 'BNPL'].map(type => ({ key: type, type, ...byType[type], baselineCount: baseline?.byType?.[type]?.count || 0 }))}
            columns={[
              { title: 'Type', dataIndex: 'type', width: 90, render: v => <Tag color={v === 'Upfront' ? 'green' : v === 'PPA' ? 'blue' : 'orange'}>{v}</Tag> },
              {
                title: 'Payment Terms', dataIndex: 'type', width: 320,
                render: v => {
                  const text = v === 'Upfront'
                    ? '10% at closing+7d · 80% at install−14d · balance at install+14d'
                    : 'Revenue at install · payment at install+14d';
                  return <span style={{ fontSize: 12, color: '#666' }}>{text}</span>;
                },
              },
              {
                title: 'GP %', dataIndex: 'cogsPct', width: 130,
                render: (v, rec) => (
                  <InputNumber
                    value={Math.round((1 - v) * 1000) / 10}
                    onChange={val => { updateType(rec.type, 'cogsPct', Math.max(0, Math.min(1, 1 - (val || 0) / 100))); }}
                    style={{ width: '100%' }}
                    min={0} max={100} step={0.1}
                    formatter={x => `${x}%`} parser={x => x.replace('%', '')}
                  />
                ),
              },
              {
                title: 'Avg Revenue / system', dataIndex: 'avgRevenue', width: 180,
                render: (v, rec) => <InputNumber value={v} onChange={val => { updateType(rec.type, 'avgRevenue', val || 0); }} style={{ width: '100%' }} formatter={moneyFmt} parser={moneyParse} />,
              },
              {
                title: 'Avg CRM Payment / system', dataIndex: 'avgCrmPayment', width: 180,
                render: (v, rec) => <InputNumber value={v} onChange={val => { updateType(rec.type, 'avgCrmPayment', val || 0); }} style={{ width: '100%' }} formatter={moneyFmt} parser={moneyParse} />,
              },
              {
                title: 'Past 3-mo Count', dataIndex: 'baselineCount', width: 130, align: 'right',
                render: v => <span style={{ color: '#999' }}>{v} systems</span>,
              },
            ]}
          />
          <Row gutter={[16, 12]} style={{ marginTop: 16 }}>
            <Col span={8}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Monthly OpEx (avg last 3 months from Xero)</div>
              <InputNumber value={monthlyOpex} onChange={v => setMonthlyOpex(v || 0)} style={{ width: '100%' }} formatter={moneyFmt} parser={moneyParse} />
              {opexDetail.length > 0 && (
                <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                  {opexDetail.map(d => `${d.period}: $${d.opex.toLocaleString()}`).join(' · ')}
                </div>
              )}
            </Col>
            <Col span={8}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Lag days (Closing → Install)</div>
              <InputNumber value={avgClosingToInstallDays} onChange={v => { setAvgClosingToInstallDays(v || 0); }} min={0} max={365} addonAfter="d" style={{ width: 140 }} />
              <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>min lag enforced before install loop assigns a date</div>
            </Col>
          </Row>
          {baseline && baseline.systemCount > 0 && (
            <div style={{ fontSize: 11, color: '#999', marginTop: 12 }}>
              Past 3 months closed: {baseline.systemCount} systems (avg {baseline.avgPerMonthCount}/mo)
            </div>
          )}
        </Spin>
      </Card>

      {/* Drivers — total sales input + auto-breakdown by mix */}
      <Card title="Drivers — total systems per month (3 months explicit, flat after). Per-type breakdown auto from past 3-mo mix." size="small" style={{ marginBottom: 16 }}>
        <Spin spinning={baselineLoading}>
        <Table
          size="small"
          pagination={false}
          dataSource={drivers.map((d, i) => {
            const breakdown = driversForEngine[i] || {};
            const actuals = monthActuals[i] || { closings: 0, installs: 0 };
            return {
              key: i, ...d,
              salesUpfront: breakdown.salesUpfront,
              salesPPA: breakdown.salesPPA,
              salesBNPL: breakdown.salesBNPL,
              monthLabel: startMonth.add(i, 'month').format('MMM YYYY'),
              actualClosings: actuals.closings,
              actualInstalls: actuals.installs,
            };
          })}
          columns={[
            { title: 'Month', dataIndex: 'monthLabel', width: 110, fixed: 'left' },
            {
              title: 'Total Sales',
              dataIndex: 'totalSales',
              width: 150,
              render: (v, rec) => {
                const floor = includeExisting ? (rec.actualClosings || 0) : 0;
                return (
                  <div>
                    <InputNumber
                      value={v}
                      onChange={val => updateDriver(rec.key, 'totalSales', Math.max(floor, val || 0))}
                      style={{ width: '100%' }}
                      min={floor}
                    />
                    {includeExisting && rec.actualClosings > 0 && (
                      <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>actual MTD: {rec.actualClosings}</div>
                    )}
                  </div>
                );
              },
            },
            {
              title: <span><Tag color="green">Upfront</Tag></span>,
              dataIndex: 'salesUpfront', width: 100, align: 'right',
              render: v => <span style={{ color: '#999' }}>{v}</span>,
            },
            {
              title: <span><Tag color="blue">PPA</Tag></span>,
              dataIndex: 'salesPPA', width: 100, align: 'right',
              render: v => <span style={{ color: '#999' }}>{v}</span>,
            },
            {
              title: <span><Tag color="orange">BNPL</Tag></span>,
              dataIndex: 'salesBNPL', width: 100, align: 'right',
              render: v => <span style={{ color: '#999' }}>{v}</span>,
            },
            {
              title: 'Total Installs (FIFO from queue, by Closing date)',
              dataIndex: 'totalInstalls',
              render: (v, rec) => {
                const floor = includeExisting ? (rec.actualInstalls || 0) : 0;
                return (
                  <div>
                    <InputNumber
                      value={v}
                      onChange={val => updateDriver(rec.key, 'totalInstalls', Math.max(floor, val || 0))}
                      style={{ width: 150 }}
                      min={floor}
                    />
                    {includeExisting && rec.actualInstalls > 0 && (
                      <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>actual MTD: {rec.actualInstalls}</div>
                    )}
                  </div>
                );
              },
            },
          ]}
        />
        <div style={{ fontSize: 11, color: '#999', marginTop: 8 }}>
          Mix: {Math.round(mix.Upfront * 1000) / 10}% Upfront / {Math.round(mix.PPA * 1000) / 10}% PPA / {Math.round(mix.BNPL * 1000) / 10}% BNPL
        </div>
        <div style={{ fontSize: 12, color: '#54A2CC', fontWeight: 600, marginTop: 12 }}>
          Pipeline at end of month:
        </div>
        <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
          {drivers.map((d, i) => {
            const c = pipelineByMonth[i] || { closingToInstall: 0, installsCompleted: 0 };
            const target = Number(d.totalInstalls) || 0;
            const shortfall = c.installsCompleted < target;
            return (
              <div key={i} style={{ marginBottom: 2 }}>
                <b>{startMonth.add(i, 'month').format('MMM YYYY')}:</b>
                {' '}Closing→Install: {c.closingToInstall}
                {' · '}Installs placed:{' '}
                <span style={{ color: shortfall ? '#FA5252' : '#05A677', fontWeight: 600 }}>
                  {c.installsCompleted}/{target}
                </span>
                {shortfall && (
                  <span style={{ color: '#FA5252', marginLeft: 6 }}>
                    (target unmet — closing→install lag ≥{avgClosingToInstallDays}d limits eligibility)
                  </span>
                )}
              </div>
            );
          })}
        </div>
        </Spin>
      </Card>

      {/* Forecast tables */}
      <Card title={<><LineChartOutlined /> Forecast P&L</>} size="small" style={{ marginBottom: 16 }}>
        <Table dataSource={pl.tableData} columns={applyRender(pl.columns)} pagination={false} size="small" scroll={{ x: 'max-content' }} rowClassName={r => r._bold ? 'xero-key-row' : ''} />
      </Card>
      <Card title={<><BankOutlined /> Forecast Balance Sheet</>} size="small" style={{ marginBottom: 16 }}>
        <Table dataSource={bs.tableData} columns={applyRender(bs.columns)} pagination={false} size="small" scroll={{ x: 'max-content' }} rowClassName={r => r._bold ? 'xero-key-row' : ''} />
      </Card>
      <Card title={<><DollarOutlined /> Forecast Cashflow</>} size="small" style={{ marginBottom: 16 }}>
        <Table dataSource={cf.tableData} columns={applyRender(cf.columns)} pagination={false} size="small" scroll={{ x: 'max-content' }} rowClassName={r => r._bold ? 'xero-key-row' : ''} />
      </Card>

      {/* Pipeline */}
      <Card
        title={
          <span>
            Project Pipeline
            <Tag color="blue" style={{ marginLeft: 8 }}>{pipelineForDisplay.length} shown</Tag>
            <Tag style={{ marginLeft: 4 }}>
              {forecast.pipeline.length} total (
              {includeExisting ? pipeline.length : 0} real + {forecast.pipeline.length - (includeExisting ? pipeline.length : 0)} forecast)
            </Tag>
          </span>
        }
        extra={
          <Space>
            <span style={{ fontSize: 12, color: '#888' }}>Closing within last:</span>
            <Select
              value={monthsBack}
              onChange={setMonthsBack}
              size="small"
              style={{ width: 120 }}
              options={[
                { value: 3, label: '3 months' },
                { value: 6, label: '6 months' },
                { value: 12, label: '12 months' },
                { value: 24, label: '24 months' },
              ]}
            />
          </Space>
        }
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Spin spinning={pipelineLoading}>
          <Table
            size="small"
            dataSource={pipelineForDisplay.map((p, i) => ({ ...p, key: p.id || i }))}
            pagination={{
              defaultPageSize: 20,
              size: 'small',
              showSizeChanger: true,
              pageSizeOptions: ['20', '50', '100', '200'],
            }}
            summary={() => {
              // Sum across ALL displayed projects (not just current page)
              const all = pipelineForDisplay;
              const sumRev = all.reduce((s, r) => s + (r.revenue || 0), 0);
              const sumPay = all.reduce((s, r) => s + (r.crmPayment || 0), 0);
              const gpPctOf = (r) => 1 - (r.cogsPct != null ? r.cogsPct : (byType[r.paymentType]?.cogsPct ?? 0.75));
              const sumGp = all.reduce((s, r) => s + (r.revenue || 0) * gpPctOf(r), 0);
              const avgGpPct = all.length ? all.reduce((s, r) => s + gpPctOf(r), 0) / all.length : 0;
              const cell = (text) => <b style={{ fontSize: 12 }}>{text}</b>;
              return (
                <Table.Summary.Row style={{ background: '#fafafa' }}>
                  <Table.Summary.Cell index={0}>{cell(`Total (${all.length})`)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={1} />
                  <Table.Summary.Cell index={2} align="right">{cell(`$${Math.round(sumRev).toLocaleString()}`)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">{cell(`$${Math.round(sumPay).toLocaleString()}`)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">{cell(`$${Math.round(sumGp).toLocaleString()}`)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right">{cell(`${Math.round(avgGpPct * 1000) / 10}%`)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={6} />
                  <Table.Summary.Cell index={7} />
                </Table.Summary.Row>
              );
            }}
            columns={[
              { title: 'Name', dataIndex: 'name', width: 200, ellipsis: true, render: (v, rec) => rec.synthetic ? <span style={{ color: '#999' }}>{v}</span> : v },
              {
                title: 'Type', dataIndex: 'paymentType', width: 90,
                render: v => <Tag color={v === 'Upfront' ? 'green' : v === 'PPA' ? 'blue' : 'orange'}>{v}</Tag>,
              },
              { title: 'CRM Revenue', dataIndex: 'revenue', width: 130, align: 'right', render: v => `$${Math.round(v || 0).toLocaleString()}` },
              { title: 'CRM Payment', dataIndex: 'crmPayment', width: 130, align: 'right', render: v => `$${Math.round(v || 0).toLocaleString()}` },
              {
                title: 'GP', width: 110, align: 'right',
                render: (_, rec) => {
                  const pct = rec.cogsPct != null ? rec.cogsPct : (byType[rec.paymentType]?.cogsPct ?? 0.75);
                  const gp = (rec.revenue || 0) * (1 - pct);
                  return `$${Math.round(gp).toLocaleString()}`;
                },
              },
              {
                title: 'GP %', width: 80, align: 'right',
                render: (_, rec) => {
                  const pct = rec.cogsPct != null ? rec.cogsPct : (byType[rec.paymentType]?.cogsPct ?? 0.75);
                  return `${Math.round((1 - pct) * 1000) / 10}%`;
                },
              },
              {
                title: 'Closing Date', dataIndex: 'closingDate', width: 120,
                render: (v, rec) => v
                  ? <span style={rec.closingEstimated ? { color: '#999', fontStyle: 'italic' } : {}}>{dayjs(v).format('D MMM YYYY')}{rec.closingEstimated ? ' *' : ''}</span>
                  : '-',
              },
              {
                title: 'Install Date', dataIndex: 'installDate', width: 130,
                render: (v, rec) => v
                  ? <span style={rec.installEstimated ? { color: '#999', fontStyle: 'italic' } : {}}>{dayjs(v).format('D MMM YYYY')}{rec.installEstimated ? ' *' : ''}</span>
                  : <span style={{ color: '#bbb' }}>(pipeline)</span>,
              },
            ]}
          />
          <div style={{ fontSize: 11, color: '#999', marginTop: 8 }}>
            <i>* italic grey = estimated date (synthetic projects, or real projects whose Install date is assigned by the install queue).</i>
          </div>
        </Spin>
      </Card>
    </>
  );
}
